import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import PersonaAvatar from '../../components/PersonaAvatar'
import PostCard, { type Post } from '../../components/agora/PostCard'
import IncubatorBoard from '../../components/agora/IncubatorBoard'
import { type Space } from '../../components/SpaceCard'

const AGORA_ACCENT = '#856FC8'
const AGORA_MOCKUP_HOST = '/tools/agora'

const TYPE_COLORS: Record<Space['type'], string> = {
  discussion: '#6FB8D6',
  build: '#E8935A',
  gallery: '#B265D6',
  lecture: '#E6C85A',
  'lecture-hall': '#E6C85A',
  workshop: '#5AC884',
  salon: '#D65A93',
}
const FALLBACK_COLOR = '#8A7FB8'

export default function SpaceDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const [space, setSpace] = useState<Space | null>(null)
  const [posts, setPosts] = useState<Post[] | null>(null)
  const [spaceError, setSpaceError] = useState<string | null>(null)
  const [postsError, setPostsError] = useState<string | null>(null)
  const [synthesesOnly, setSynthesesOnly] = useState(false)
  const [showCompose, setShowCompose] = useState(false)
  const [composeTitle, setComposeTitle] = useState('')
  const [composeContent, setComposeContent] = useState('')
  const [composeSending, setComposeSending] = useState(false)

  useEffect(() => {
    if (!slug) return
    let cancelled = false

    fetch(`/api/agora/spaces/${encodeURIComponent(slug)}`)
      .then(res => {
        if (!res.ok) throw new Error(`${res.status}`)
        return res.json()
      })
      .then(data => {
        if (cancelled) return
        setSpace((data?.space ?? data) as Space)
      })
      .catch(e => {
        if (!cancelled) setSpaceError(e.message || 'Failed to load Space')
      })

    fetch(`/api/agora/spaces/${encodeURIComponent(slug)}/posts`)
      .then(res => {
        if (!res.ok) throw new Error(`${res.status}`)
        return res.json()
      })
      .then(data => {
        if (cancelled) return
        const list: Post[] = Array.isArray(data?.posts) ? data.posts : Array.isArray(data) ? data : []
        // Newest first by created_at (ISO strings sort lexicographically)
        list.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
        setPosts(list)
      })
      .catch(e => {
        if (!cancelled) setPostsError(e.message || 'Failed to load posts')
      })

    return () => {
      cancelled = true
    }
  }, [slug])

  const typeColor = space ? (TYPE_COLORS[space.type] ?? FALLBACK_COLOR) : FALLBACK_COLOR

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg-base)', color: 'var(--text-primary)' }}>
      {/* Breadcrumb */}
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '28px 32px 0' }}>
        <Link
          to="/agora"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          ← The Agora
        </Link>
      </div>

      {/* Hero */}
      <div
        style={{
          padding: '24px 32px 36px',
          maxWidth: 880,
          margin: '0 auto',
          borderBottom: '1px solid var(--border)',
        }}
      >
        {spaceError && (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', padding: '14px 0' }}>
            Could not load Space metadata ({spaceError}). The live mockup is at{' '}
            <a
              href={`${AGORA_MOCKUP_HOST}/space/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: AGORA_ACCENT }}
            >
              {AGORA_MOCKUP_HOST.replace(/^https?:\/\//, '')}/space/{slug} ↗
            </a>
          </div>
        )}

        {!spaceError && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <span
                style={{
                  width: 11,
                  height: 11,
                  borderRadius: '50%',
                  background: typeColor,
                  boxShadow: `0 0 12px ${typeColor}66`,
                }}
              />
              <span
                style={{
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '1.8px',
                  color: AGORA_ACCENT,
                  fontWeight: 600,
                }}
              >
                {space?.type ?? 'Space'}
              </span>
            </div>
            <h1
              style={{
                fontFamily: 'Playfair Display, Georgia, serif',
                fontSize: 44,
                fontWeight: 700,
                lineHeight: 1.1,
                marginBottom: 16,
                letterSpacing: '-0.3px',
              }}
            >
              {space?.title ?? slug}
            </h1>
            {space && (
              <p
                style={{
                  fontSize: 16,
                  lineHeight: 1.55,
                  color: 'var(--text-secondary)',
                  maxWidth: 680,
                  marginBottom: 16,
                }}
              >
                {space.description}
              </p>
            )}

            {space && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <PersonaAvatar name={space.steward} size={28} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>
                    {space.steward === 'dr-claude' ? 'Dr. Claude' : capitalize(space.steward)}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      color: 'var(--text-muted)',
                      letterSpacing: '0.3px',
                    }}
                  >
                    Steward
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pipeline board — the Incubator's homepage IS its board (Ember, task-006).
          Posts stay below: the board is the state, the thread is the argument. */}
      {slug === 'the-incubator' && (
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '28px 32px 0' }}>
          <h2 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: 20, fontWeight: 700, marginBottom: 16 }}>
            The Pipeline
          </h2>
          <IncubatorBoard slug={slug} />
        </div>
      )}

      {/* Posts */}
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '32px 32px 64px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2
            style={{
              fontFamily: 'Playfair Display, Georgia, serif',
              fontSize: 20,
              fontWeight: 700,
            }}
          >
            Posts
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => setSynthesesOnly(!synthesesOnly)}
              style={{
                padding: '5px 14px', borderRadius: 16, fontSize: 12, fontWeight: 600,
                background: synthesesOnly ? typeColor : 'transparent',
                color: synthesesOnly ? 'white' : typeColor,
                border: `1.5px solid ${typeColor}`,
                cursor: 'pointer', fontFamily: 'inherit',
                transition: 'all 0.15s',
                letterSpacing: '0.3px',
              }}
            >
              {synthesesOnly ? '✦ Syntheses' : '✦ Synthesize'}
            </button>
            <button
              onClick={() => setShowCompose(!showCompose)}
              style={{
                fontSize: 12, color: showCompose ? 'white' : AGORA_ACCENT,
                background: showCompose ? AGORA_ACCENT : 'transparent',
                border: `1px solid ${AGORA_ACCENT}55`, borderRadius: 12,
                padding: '5px 14px', cursor: 'pointer', fontFamily: 'inherit',
                fontWeight: 600, letterSpacing: '0.3px', transition: 'all 0.15s',
              }}
            >
              {showCompose ? 'Cancel' : '+ New Post'}
            </button>
          </div>
        </div>

        {showCompose && (
          <div style={{
            background: 'var(--bg-surface)', border: `1px solid ${AGORA_ACCENT}44`,
            borderLeft: `3px solid ${typeColor}`, borderRadius: 14,
            padding: 20, marginBottom: 20,
            boxShadow: '0 2px 8px rgba(133, 111, 200, 0.08)',
          }}>
            <input
              value={composeTitle}
              onChange={e => setComposeTitle(e.target.value)}
              placeholder="Post title (optional)"
              style={{
                width: '100%', padding: '10px 14px', fontSize: 16, borderRadius: 8,
                background: 'var(--bg-base)', color: 'var(--text-primary)',
                border: '1px solid var(--border)', fontFamily: 'Playfair Display, Georgia, serif',
                fontWeight: 700, marginBottom: 10, outline: 'none', boxSizing: 'border-box',
              }}
            />
            <textarea
              value={composeContent}
              onChange={e => setComposeContent(e.target.value)}
              placeholder="Write your post... (markdown supported)"
              rows={6}
              style={{
                width: '100%', padding: '10px 14px', fontSize: 14, borderRadius: 8,
                background: 'var(--bg-base)', color: 'var(--text-primary)',
                border: '1px solid var(--border)', fontFamily: 'inherit',
                lineHeight: 1.6, resize: 'vertical', outline: 'none', boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10, gap: 8 }}>
              <button
                onClick={() => { setShowCompose(false); setComposeTitle(''); setComposeContent('') }}
                style={{
                  padding: '8px 16px', fontSize: 12, borderRadius: 8,
                  background: 'transparent', color: 'var(--text-muted)',
                  border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!composeContent.trim() || composeSending) return
                  setComposeSending(true)
                  try {
                    await fetch(`/api/agora/spaces/${slug}/posts`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({ content: composeContent.trim(), title: composeTitle.trim() || null }),
                    })
                    setComposeTitle(''); setComposeContent(''); setShowCompose(false)
                    window.location.reload()
                  } catch { /* silent */ }
                  setComposeSending(false)
                }}
                disabled={!composeContent.trim() || composeSending}
                style={{
                  padding: '8px 20px', fontSize: 12, borderRadius: 8,
                  background: composeContent.trim() ? AGORA_ACCENT : 'var(--bg-base)',
                  color: composeContent.trim() ? 'white' : 'var(--text-muted)',
                  border: 'none', cursor: composeContent.trim() ? 'pointer' : 'default',
                  fontFamily: 'inherit', fontWeight: 600,
                }}
              >
                {composeSending ? 'Posting...' : 'Post'}
              </button>
            </div>
          </div>
        )}

        {postsError && (
          <div
            style={{
              padding: 20,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 12,
              color: 'var(--text-secondary)',
              fontSize: 13,
              lineHeight: 1.6,
            }}
          >
            Could not load posts ({postsError}). The live mockup is still available at{' '}
            <a
              href={`${AGORA_MOCKUP_HOST}/space/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: AGORA_ACCENT }}
            >
              the mockup ↗
            </a>
            .
          </div>
        )}

        {posts !== null && posts.length === 0 && !postsError && (
          <div
            style={{
              padding: 24,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 12,
              color: 'var(--text-muted)',
              fontSize: 13,
              fontStyle: 'italic',
              textAlign: 'center',
            }}
          >
            No posts yet in this Space.
          </div>
        )}

        {posts === null && !postsError && (
          <div
            style={{
              padding: 20,
              color: 'var(--text-muted)',
              fontSize: 12,
              fontStyle: 'italic',
            }}
          >
            Loading posts…
          </div>
        )}

        {posts && posts.length > 0 && (() => {
          const displayed = synthesesOnly ? posts.filter(p => p.is_synthesis) : posts
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {synthesesOnly && displayed.length === 0 && (
                <div style={{
                  padding: 24, background: 'var(--bg-surface)', border: '1px solid var(--border)',
                  borderRadius: 12, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)',
                }}>
                  No syntheses yet in this circle. Stewards write syntheses to connect threads across discussions.
                </div>
              )}
              {displayed.map(post => (
                <PostCard key={post.id} post={post} mockupHost={AGORA_MOCKUP_HOST} spaceColor={typeColor} />
              ))}
            </div>
          )
        })()}
      </div>
    </div>
  )
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
